// src/components/dashboard/WaitlistTab.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton
} from "@mui/material";
import axiosInstance from "../../utils/axiosConfig";
import WaitlistPromoteEmailDialog from './WaitlistPromoteEmailDialog';
import WaitlistInquiryEmailDialog from './WaitlistInquiryEmailDialog';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { handleApiError } from "../../utils/apiErrorHandler";
import { toast } from "../../utils/toast";

interface WaitlistTabProps {
  competitionId: string;
}

interface WaitlistedAthlete {
  athleteId: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
  competitionGroup: string;
  addedAt: string;
}

interface CompetitionGroup {
  _id: string;
  name: string;
  type: "Elite" | "Basis";
  gender: string;
}

interface ClubWaitlist {
  _id: string; // Waitlist entry ID
  clubId: string;
  clubName: string;
  type: "Elite" | "Basis";
  isActive: boolean;
  athletes: WaitlistedAthlete[];
  updatedAt: string;
}

interface PromoteDialogState {
  open: boolean;
  club: ClubWaitlist | null;
  athlete: WaitlistedAthlete | null;
  allAthletes: boolean;
}

interface ToggleActiveDialogState {
  open: boolean;
  entry: ClubWaitlist | null;
  action: 'activate' | 'deactivate';
}

interface DeleteDialogState {
  open: boolean;
  entry: ClubWaitlist | null;
  athlete: WaitlistedAthlete | null; // null = ganzen Eintrag löschen
}

const WaitlistTab: React.FC<WaitlistTabProps> = ({ competitionId }) => {
  const [waitlistEntries, setWaitlistEntries] = useState<ClubWaitlist[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<"Elite" | "Basis">("Elite");
  const [showInactive, setShowInactive] = useState<boolean>(true);
  const [competitionGroups, setCompetitionGroups] = useState<CompetitionGroup[]>([]);
  
  // Dialog state for confirmation when promoting athletes
  const [promoteDialog, setPromoteDialog] = useState<PromoteDialogState>({
    open: false,
    club: null,
    athlete: null,
    allAthletes: false
  });

  // Dialog state for toggling active status
  const [toggleActiveDialog, setToggleActiveDialog] = useState<ToggleActiveDialogState>({
    open: false,
    entry: null,
    action: 'deactivate'
  });

  // Dialog state for deleting entry or athlete
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    entry: null,
    athlete: null
  });

  // Dialog state for waitlist inquiry email
  const [inquiryDialog, setInquiryDialog] = useState<{ open: boolean; club: ClubWaitlist | null }>({
    open: false,
    club: null
  });

  // Fetch waitlist data and competition groups on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // First fetch competition groups to get group names
        const groupsResponse = await axiosInstance.get(`/api/competitions/${competitionId}`);
        if (groupsResponse.data && groupsResponse.data.competitionGroups) {
          setCompetitionGroups(groupsResponse.data.competitionGroups);
        }
        
        // Then fetch waitlist entries
        const waitlistResponse = await axiosInstance.get(`/api/waitlist/competition/${competitionId}`);
        
        // Process the response data to group by club
        const entries: ClubWaitlist[] = [];
        
        for (const entry of waitlistResponse.data) {
          // Show active entries only if they have athletes
          // Show inactive entries always (to see inactive status)
          const hasAthletes = entry.athletes && entry.athletes.length > 0;
          const isActive = entry.isActive !== false; // Default to true if not specified
          
          if (hasAthletes || !isActive) {
            entries.push({
              _id: entry._id,
              clubId: entry.club._id,
              clubName: entry.club.name,
              type: entry.type,
              isActive: isActive,
              athletes: entry.athletes || [],
              updatedAt: entry.updatedAt
            });
          }
        }
        
        setWaitlistEntries(entries);
        setError(null);
      } catch (err) {
        handleApiError(err, "Fehler beim Laden der Warteliste");
        setError("Fehler beim Laden der Warteliste");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [competitionId]);
  
  // Get competition group name from ID
  const getGroupName = (groupId: string): string => {
    const group = competitionGroups.find(g => g._id === groupId);
    return group ? group.name : groupId; // Fallback to ID if name not found
  };
  
  const getGenderLabel = (gender: string) => {
    switch (gender) {
      case 'm':
        return 'männlich';
      case 'w':
        return 'weiblich';
      default:
        return gender;
    }
  };
  
  // Open dialog to confirm promoting an athlete
  const handlePromoteAthlete = (club: ClubWaitlist, athlete: WaitlistedAthlete) => {
    if (!club.isActive) {
      toast.warning("Athleten aus inaktiven Wartelisten können nicht übernommen werden.");
      return;
    }
    
    setPromoteDialog({
      open: true,
      club,
      athlete,
      allAthletes: false
    });
  };
  
  // Open dialog to confirm promoting all athletes from a club
  const handlePromoteAllAthletes = (club: ClubWaitlist) => {
    if (!club.isActive) {
      toast.warning("Athleten aus inaktiven Wartelisten können nicht übernommen werden.");
      return;
    }
    
    setPromoteDialog({
      open: true,
      club,
      athlete: null,
      allAthletes: true
    });
  };
  
  // Close the promotion dialog
  const handleClosePromoteDialog = () => {
    setPromoteDialog({
      open: false,
      club: null,
      athlete: null,
      allAthletes: false
    });
  };

  // Open dialog to confirm toggling active status
  const handleToggleActiveStatus = (entry: ClubWaitlist, action: 'activate' | 'deactivate') => {
    setToggleActiveDialog({
      open: true,
      entry,
      action
    });
  };

  // Close the toggle active dialog
  const handleCloseToggleActiveDialog = () => {
    setToggleActiveDialog({
      open: false,
      entry: null,
      action: 'deactivate'
    });
  };

  // Confirm toggling active status
  const handleConfirmToggleActive = async () => {
    if (!toggleActiveDialog.entry) return;

    try {
      const endpoint = toggleActiveDialog.action === 'activate' ? 'reactivate' : 'deactivate';
      
      await axiosInstance.patch(`/api/waitlist/${toggleActiveDialog.entry._id}/${endpoint}`);
      
      // Update local state
      setWaitlistEntries(prev => 
        prev.map(entry => 
          entry._id === toggleActiveDialog.entry?._id 
            ? { ...entry, isActive: toggleActiveDialog.action === 'activate' }
            : entry
        )
      );
      
      const actionText = toggleActiveDialog.action === 'activate' ? 'aktiviert' : 'deaktiviert';
      toast.success(`Warteliste von ${toggleActiveDialog.entry.clubName} wurde ${actionText}.`);
      
    } catch (err) {
      handleApiError(err, "Fehler beim Ändern des Status");
    } finally {
      handleCloseToggleActiveDialog();
    }
  };
  
  const handleDeleteEntry = (entry: ClubWaitlist) => {
    setDeleteDialog({ open: true, entry, athlete: null });
  };

  const handleDeleteAthlete = (entry: ClubWaitlist, athlete: WaitlistedAthlete) => {
    setDeleteDialog({ open: true, entry, athlete });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, entry: null, athlete: null });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.entry) return;
    try {
      if (deleteDialog.athlete) {
        await axiosInstance.delete(
          `/api/waitlist/entry/${deleteDialog.entry._id}/athlete/${deleteDialog.athlete.athleteId}`
        );
        setWaitlistEntries(prev =>
          prev.map(e =>
            e._id === deleteDialog.entry?._id
              ? { ...e, athletes: e.athletes.filter(a => a.athleteId !== deleteDialog.athlete?.athleteId) }
              : e
          ).filter(e => e.isActive || e.athletes.length > 0)
        );
        toast.success(`${deleteDialog.athlete.firstname} ${deleteDialog.athlete.surname} von der Warteliste entfernt.`);
      } else {
        await axiosInstance.delete(`/api/waitlist/entry/${deleteDialog.entry._id}`);
        setWaitlistEntries(prev => prev.filter(e => e._id !== deleteDialog.entry?._id));
        toast.success(`Warteliste von ${deleteDialog.entry.clubName} gelöscht.`);
      }
    } catch (err) {
      handleApiError(err, 'Fehler beim Löschen.');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // Called by WaitlistPromoteEmailDialog after successful promotion
  const handlePromoted = (promotedIds: string[]) => {
    if (!promoteDialog.club) return;

    if (promoteDialog.allAthletes) {
      setWaitlistEntries(prev =>
        prev.map(entry =>
          entry._id === promoteDialog.club?._id ? { ...entry, athletes: [] } : entry
        )
      );
    } else {
      setWaitlistEntries(prev =>
        prev.map(entry => {
          if (entry._id === promoteDialog.club?._id) {
            return {
              ...entry,
              athletes: entry.athletes.filter(a => !promotedIds.includes(a.athleteId))
            };
          }
          return entry;
        })
      );
    }

    handleClosePromoteDialog();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Filter entries by type and active status
  const filteredEntries = waitlistEntries.filter(entry => {
    const typeMatch = entry.type === activeType;
    const activeMatch = showInactive || entry.isActive;
    return typeMatch && activeMatch;
  });

  const activeEntries = filteredEntries.filter(entry => entry.isActive);
  const inactiveEntries = filteredEntries.filter(entry => !entry.isActive);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        {/* Type Filter */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button 
            variant={activeType === "Elite" ? "contained" : "outlined"}
            onClick={() => setActiveType("Elite")}
          >
            Elite
          </Button>
          <Button 
            variant={activeType === "Basis" ? "contained" : "outlined"}
            onClick={() => setActiveType("Basis")}
          >
            Basis
          </Button>
        </Box>

        {/* Active Status Filter */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                color="primary"
              />
            }
            label="Inaktive Wartelisten anzeigen"
          />
          
          <Tooltip title={showInactive ? "Inaktive ausblenden" : "Inaktive einblenden"}>
            <IconButton onClick={() => setShowInactive(!showInactive)} size="small">
              {showInactive ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Athleten auf der Warteliste können zur Meldeliste hinzugefügt werden, wenn Plätze verfügbar sind.
          {inactiveEntries.length > 0 && (
            <> Inaktive Wartelisten ({inactiveEntries.length}) sind ausgegraut dargestellt.</>
          )}
        </Typography>

        {/* Summary */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip 
            icon={<CheckCircleIcon />}
            label={`${activeEntries.length} aktive Wartelisten`} 
            color="success"
            variant="outlined"
          />
          {inactiveEntries.length > 0 && (
            <Chip 
              icon={<BlockIcon />}
              label={`${inactiveEntries.length} inaktive Wartelisten`} 
              color="default"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {filteredEntries.length === 0 ? (
        <Alert severity="info">
          {showInactive 
            ? `Keine Wartelisten für ${activeType} gefunden.`
            : `Keine aktiven Wartelisten für ${activeType} gefunden.`}
        </Alert>
      ) : (
        filteredEntries.map((entry) => (
          <Accordion 
            key={`${entry._id}`} 
            sx={{ 
              mb: 1,
              opacity: entry.isActive ? 1 : 0.6,
              backgroundColor: entry.isActive ? 'inherit' : 'rgba(0, 0, 0, 0.04)',
              '& .MuiAccordionDetails-root': {
                padding: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '&:hover': {
                  backgroundColor: entry.isActive ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%',
                pr: 2 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 'medium',
                      textDecoration: entry.isActive ? 'none' : 'line-through',
                      color: entry.isActive ? 'inherit' : 'text.secondary'
                    }}
                  >
                    {entry.clubName}
                  </Typography>
                  
                  {!entry.isActive && (
                    <Chip
                      icon={<BlockIcon />}
                      label="Inaktiv"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<GroupIcon />}
                    label={`${entry.athletes.length} Athleten`}
                    size="small"
                    color={entry.isActive ? "primary" : "default"}
                    variant={entry.isActive ? "filled" : "outlined"}
                  />
                  
                  {entry.isActive ? (
                    <>
                      {entry.athletes.length > 0 && (
                        <>
                          <Button
                            variant="contained"
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePromoteAllAthletes(entry);
                            }}
                          >
                            Alle übernehmen
                          </Button>
                          <Tooltip title="Rückfrage per E-Mail senden">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                setInquiryDialog({ open: true, club: entry });
                              }}
                            >
                              <ContactMailIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<BlockIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleActiveStatus(entry, 'deactivate');
                        }}
                      >
                        Deaktivieren
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleActiveStatus(entry, 'activate');
                      }}
                    >
                      Aktivieren
                    </Button>
                  )}
                  <Tooltip title="Gesamte Warteliste löschen">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEntry(entry);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              {entry.athletes.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Keine Athleten auf der Warteliste
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Jahrgang</TableCell>
                        <TableCell>Geschlecht</TableCell>
                        <TableCell>Wettkampfklasse</TableCell>
                        <TableCell>Hinzugefügt am</TableCell>
                        <TableCell align="right">Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entry.athletes.map((athlete) => (
                        <TableRow 
                          key={athlete.athleteId}
                          sx={{ 
                            opacity: entry.isActive ? 1 : 0.7,
                            '& td': {
                              color: entry.isActive ? 'inherit' : 'text.secondary'
                            }
                          }}
                        >
                          <TableCell>
                            {athlete.firstname} {athlete.surname}
                          </TableCell>
                          <TableCell>{athlete.age}</TableCell>
                          <TableCell>{getGenderLabel(athlete.gender)}</TableCell>
                          <TableCell>{getGroupName(athlete.competitionGroup)}</TableCell>
                          <TableCell>
                            {new Date(athlete.addedAt).toLocaleDateString('de-DE')}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={!entry.isActive}
                                onClick={() => handlePromoteAthlete(entry, athlete)}
                              >
                                Übernehmen
                              </Button>
                              <Tooltip title="Athlet von Warteliste entfernen">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteAthlete(entry, athlete)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <WaitlistPromoteEmailDialog
        open={promoteDialog.open}
        onClose={handleClosePromoteDialog}
        competitionId={competitionId}
        club={promoteDialog.club}
        athlete={promoteDialog.athlete}
        allAthletes={promoteDialog.allAthletes}
        onPromoted={handlePromoted}
      />

      {/* Dialog: Warteliste oder Athlet löschen */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          {deleteDialog.athlete ? 'Athlet von Warteliste entfernen' : 'Warteliste löschen'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteDialog.athlete
              ? <>Soll <strong>{deleteDialog.athlete.firstname} {deleteDialog.athlete.surname}</strong> wirklich von der Warteliste entfernt werden?</>
              : <>Soll die gesamte Warteliste ({deleteDialog.entry?.type}) von <strong>{deleteDialog.entry?.clubName}</strong> wirklich gelöscht werden? Alle {deleteDialog.entry?.athletes.length} Athleten werden entfernt.</>
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">Abbrechen</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for confirming active status toggle */}
      <Dialog
        open={toggleActiveDialog.open}
        onClose={handleCloseToggleActiveDialog}
      >
        <DialogTitle>
          {toggleActiveDialog.action === 'activate'
            ? "Warteliste aktivieren"
            : "Warteliste deaktivieren"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toggleActiveDialog.action === 'activate'
              ? `Möchten Sie die Warteliste von ${toggleActiveDialog.entry?.clubName} wieder aktivieren? Die Athleten können dann wieder zur Meldeliste hinzugefügt werden.`
              : `Möchten Sie die Warteliste von ${toggleActiveDialog.entry?.clubName} deaktivieren? Die Athleten können dann nicht mehr zur Meldeliste hinzugefügt werden.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseToggleActiveDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            color={toggleActiveDialog.action === 'activate' ? 'success' : 'warning'}
            onClick={handleConfirmToggleActive}
          >
            {toggleActiveDialog.action === 'activate' ? 'Aktivieren' : 'Deaktivieren'}
          </Button>
        </DialogActions>
      </Dialog>

      <WaitlistInquiryEmailDialog
        open={inquiryDialog.open}
        onClose={() => setInquiryDialog({ open: false, club: null })}
        competitionId={competitionId}
        club={inquiryDialog.club}
        onSent={() => {}}
      />
    </Box>
  );
};

export default WaitlistTab;